import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { API_CONFIG } from '../config/api.config';
import { ApiEnvelope, unwrapApiData } from '../models/api-response.model';
import { isGalleryVideoUrl, toTypedVideoBlob } from '../utils/media.util';

type FileViewOptions = {
  visibility?: 'public' | 'private';
};

@Injectable({ providedIn: 'root' })
export class FileViewService {
  constructor(private readonly http: HttpClient) {}

  /** Resolve public project gallery images (no auth required when public CDN is configured). */
  getProjectPhotoUrl(storedKeyOrUrl: string | null | undefined): Observable<string> {
    const value = (storedKeyOrUrl ?? '').trim();
    if (!value) {
      return of('');
    }
    if (/^https?:\/\//i.test(value)) {
      return of(value);
    }

    const params = new HttpParams().set('key', value);
    return this.http
      .get<ApiEnvelope<Record<string, unknown>>>(
        `${environment.apiUrl}${API_CONFIG.files.publicUrl}`,
        { params },
      )
      .pipe(
        switchMap((res) => {
          const inner = (unwrapApiData(res) ?? res) as Record<string, unknown>;
          const publicUrl = typeof inner['url'] === 'string' ? inner['url'].trim() : '';
          if (publicUrl) {
            return of(publicUrl);
          }
          return this.getViewableFileUrl(value, { visibility: 'public' });
        }),
        catchError(() => this.getViewableFileUrl(value, { visibility: 'public' })),
      );
  }

  getViewableFileUrl(
    storedKeyOrUrl: string | null | undefined,
    options?: FileViewOptions,
  ): Observable<string> {
    const value = (storedKeyOrUrl ?? '').trim();
    if (!value) {
      return of('');
    }
    if (/^https?:\/\//i.test(value)) {
      return of(value);
    }

    let params = new HttpParams().set('key', value);
    if (options?.visibility === 'public') {
      params = params.set('visibility', 'public');
    }

    return this.http
      .get<ApiEnvelope<Record<string, unknown>>>(
        `${environment.apiUrl}${API_CONFIG.files.previewUrl}`,
        { params },
      )
      .pipe(
        switchMap((res) => {
          const inner = (unwrapApiData(res) ?? res) as Record<string, unknown>;
          const previewUrl = typeof inner['url'] === 'string' ? inner['url'].trim() : '';
          if (previewUrl) {
            return of(previewUrl);
          }
          return this.streamBlobToObjectUrl(value, params);
        }),
        catchError(() => this.streamBlobToObjectUrl(value, params)),
      );
  }

  /**
   * Stream file bytes via GET /v1/files/stream — avoids preview-url S3/CDN responses.
   */
  streamStoredFileBlob(storedKeyOrUrl: string | null | undefined): Observable<string> {
    const key = this.normalizeStorageKey(storedKeyOrUrl);
    if (!key || /^https?:\/\//i.test(key)) {
      return of('');
    }

    const params = new HttpParams().set('key', key);
    return this.streamBlobToObjectUrl(key, params);
  }

  private streamBlobToObjectUrl(storageKey: string, params: HttpParams): Observable<string> {
    return this.http
      .get(`${environment.apiUrl}${API_CONFIG.files.stream}`, {
        params,
        responseType: 'blob',
      })
      .pipe(
        map((blob) => {
          const typedBlob = isGalleryVideoUrl(storageKey) ? toTypedVideoBlob(blob, storageKey) : blob;
          return URL.createObjectURL(typedBlob);
        }),
        catchError(() => of('')),
      );
  }

  normalizeStorageKey(raw: string | null | undefined): string {
    const value = (raw ?? '').trim();
    if (!value) {
      return '';
    }
    if (!/^https?:\/\//i.test(value)) {
      return value.replace(/^\/+/, '');
    }
    try {
      const path = decodeURIComponent(new URL(value).pathname).replace(/^\/+/, '');
      return path || value;
    } catch {
      return value;
    }
  }

  revokeIfBlob(url: string | null | undefined): void {
    if (url?.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  }

  revokeAllBlobUrls(urls: string[]): void {
    for (const url of urls) {
      this.revokeIfBlob(url);
    }
  }
}
